import { Digit, Fraction, LieDigit, LieFraction, LoftDigit, LoftFraction } from '@/api/models';
import { sortByPropKey } from '@/utils';

interface IJoinedDigitTableMap {
  name: string | undefined;
  joinTableDigitId: number;
  sourceTableDigitId: number | undefined;
}

interface IJoinedFractionTableMap {
  name: string | undefined;
  joinTableFractionId: number;
  sourceTableFractionId: number | undefined;
}

type LoftLieDigitUnion = LoftDigit | LieDigit;

const joinDigits = (joinTableDigits: LoftLieDigitUnion[], headTypeId: number, sourceTableDigit: Digit[]): IJoinedDigitTableMap[] => {
  return joinTableDigits
    .filter((joinTabledigit) => joinTabledigit.headTypeId === headTypeId)
    .map((joinTableDigit) => {
      const matchedDigit = sourceTableDigit.find((sourceTable) => sourceTable.digitId === joinTableDigit.digitId);
      const joinTableDigitId = 'lieDigitId' in joinTableDigit ? joinTableDigit.lieDigitId : joinTableDigit.loftDigitId;
      return {
        name: matchedDigit?.name,
        joinTableDigitId,
        sourceTableDigitId: joinTableDigit.digitId,
      };
    });
};

type LoftLieFractionUnion = LoftFraction | LieFraction;

const joinFractions = (joinTableFractions: LoftLieFractionUnion[], headTypeId: number, sourceTableFraction: Fraction[]): IJoinedFractionTableMap[] => {
  return joinTableFractions
    .filter((joinTableFraction) => joinTableFraction.headTypeId === headTypeId)
    .map((joinTableFraction) => {
      const matchedFraction = sourceTableFraction.find((sourceTable) => sourceTable.fractionId === joinTableFraction.fractionId);
      const joinTableFractionId = 'lieFractionId' in joinTableFraction ? joinTableFraction.lieFractionId : joinTableFraction.loftFractionId;
      return {
        name: matchedFraction?.name,
        joinTableFractionId,
        sourceTableFractionId: joinTableFraction.fractionId,
      };
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

interface ILoftDropdownOption {
  loftDigit: string | undefined;
  loftFraction: string | undefined;
  joinTableDigitId?: number;
  sourceTableDigitId?: number;
  joinTableFractionId?: number;
  sourceTableFractionId?: number;
}

interface ILieDropdownOption {
  lieDigit: string | undefined;
  lieFraction: string | undefined;
  joinTableDigitId?: number;
  sourceTableDigitId?: number;
  joinTableFractionId?: number;
  sourceTableFractionId?: number;
}

export interface ILoftOptionMap {
  map: Record<string, ILoftDropdownOption>;
  options: Array<{
    id: string;
    label: string;
  }>;
}

export interface ILieOptionMap {
  map: Record<string, ILieDropdownOption>;
  options: Array<{
    id: string;
    label: string;
  }>;
}

interface IBuildDropdownData {
  headTypeId: number;
  digitSourceTable: Digit[];
  fractionSourceTable: Fraction[];
  lieDigitJoinTable: LieDigit[];
  lieFractionJoinTable: LieFraction[];
  loftDigitJoinTable: LoftDigit[];
  loftFractionJoinTable: LoftFraction[];
}

// ---- joinTableId = id ----
// ---- sourceTableId = id1 ----

export const buildDropdownData = ({ headTypeId, digitSourceTable, fractionSourceTable, lieDigitJoinTable, lieFractionJoinTable, loftDigitJoinTable, loftFractionJoinTable }: IBuildDropdownData) => {
  // join digit & fraction tables with lie & loft digit and fraction join tables
  const joinedLoftDigits = joinDigits(loftDigitJoinTable, headTypeId, digitSourceTable);
  const joinedLoftFractions = joinFractions(loftFractionJoinTable, headTypeId, fractionSourceTable);
  const joinedLieDigits = joinDigits(lieDigitJoinTable, headTypeId, digitSourceTable);
  const joinedLieFractions = joinFractions(lieFractionJoinTable, headTypeId, fractionSourceTable);

  const loftDropdownOptionMap = joinedLoftDigits.sort(sortByPropKey<IJoinedDigitTableMap>('name')).reduce<ILoftOptionMap>(
    (acc, loftDigit) => {
      joinedLoftFractions.forEach((loftFraction) => {
        const value = `${loftDigit.name}${loftFraction.name}`; // value acts as a unique id given a digit + fraction combination ie '50.00'

        // options map
        acc.map[value] = {
          loftDigit: loftDigit.name,
          loftFraction: loftFraction.name,
          joinTableDigitId: loftDigit.joinTableDigitId,
          sourceTableDigitId: loftDigit.sourceTableDigitId,
          joinTableFractionId: loftFraction.joinTableFractionId,
          sourceTableFractionId: loftFraction.sourceTableFractionId,
        };

        // dropdown options
        acc.options.push({ id: value, label: value });
      });
      return acc;
    },
    { map: {}, options: [] },
  );

  const lieDropdownOptionMap = joinedLieDigits.sort(sortByPropKey<IJoinedDigitTableMap>('name')).reduce<ILieOptionMap>(
    (acc, lieDigit) => {
      joinedLieFractions.forEach((lieFraction) => {
        const value = `${lieDigit.name}${lieFraction.name}`; // value acts as a unique id given a digit + fraction combination ie '50.00'

        // options map
        acc.map[value] = {
          lieDigit: lieDigit.name,
          lieFraction: lieFraction.name,
          joinTableDigitId: lieDigit.joinTableDigitId,
          sourceTableDigitId: lieDigit.sourceTableDigitId,
          joinTableFractionId: lieFraction.joinTableFractionId,
          sourceTableFractionId: lieFraction.sourceTableFractionId,
        };

        // dropdown options
        acc.options.push({ id: value, label: value });
      });
      return acc;
    },
    { map: {}, options: [] },
  );

  return {
    loftDropdownOptionMap,
    lieDropdownOptionMap,
  };
};
